// Libraries
import { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// @mui material components
import Grid from "@mui/material/Grid2";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { scrollToTop, useHandleError } from "components/helper";
// Redux Actions

import setAlert from "store/actions/alertActions";
import { addTrend, clearErrors } from "store/actions/uniqueExperienceActions";

// Layouts
import DashboardLayout from "layouts/DashboardLayout";

// Custom Components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardNavbar from "components/DashboardNavbar";
import Footer from "components/Footer";

// Schema

// import validations from "./schema/validations";
import form from "./schema/form";
import initialValues from "./schema/initialValues";
import Information from "./components/Information";
import Review from "./components/Review";
import buildSchema from "./schema/validations";

function getSteps() {
  return ["Information", "Review"];
}

function getStepContent(
  stepIndex,
  formData,
  handleItemChange,
  handleRecommendationChange,
  handleItineraryChange,
  selectedItem,
  selectedRecommendation,
  selectedItenerary
) {
  switch (stepIndex) {
    case 0:
      return (
        <Information
          formData={formData}
          selectedItem={selectedItem}
          selectedRecommendation={selectedRecommendation}
          selectedItenerary={selectedItenerary}
          handleItemChange={handleItemChange}
          handleRecommendationChange={handleRecommendationChange}
          handleItineraryChange={handleItineraryChange}
        />
      );
    case 1:
      return (
        <Review
          formData={formData}
          selectedItem={selectedItem}
          selectedRecommendation={selectedRecommendation}
        />
      );
    default:
      return null;
  }
}

const NewUniqieExperience = () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // selector
  const error = useSelector((state) => state.trends.error);
  const trendObj = useSelector((state) => state.trends.trendObj);

  // state
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItenerary, setSelectedItenerary] = useState(null);
  // const [type, setType] = useState("");

  // Update validation schema based on current state
  // const currentValidation = useMemo(() => validations(trendObj?.isTrip, type), [trendObj]);

  const { formId, formField } = form;
  // const currentValidation = validations[0];
  const isLastStep = activeStep === steps.length - 1;

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    scrollToTop(); // Scroll to the top
  };

  // Update type state when form values change
  // useEffect(() => {
  //   if (formRef.current?.values?.type) {
  //     setType(formRef.current.values.type);
  //   }
  // }, [formRef.current?.values?.type]);

  const submitForm = async (values, actions) => {
    await sleep(1000);
    const updatedValues = { ...values };
    let finalTrendDataForApi = {};

    if (trendObj?.isTrip) {
      // Handling for 'experience' (Itinerary)
      if (updatedValues.itinerary && updatedValues.itinerary._id) {
        finalTrendDataForApi = {
          type: "experience",
          itemId: updatedValues.itinerary._id,
          trendingStart: updatedValues.trendingStart,
        };
        if (updatedValues.trendingEnd && updatedValues.trendingEnd.length > 0) {
          finalTrendDataForApi.trendingEnd = updatedValues.trendingEnd;
        }
        // Add location ID if trendObj.value exists and it's NOT a destination
        if (trendObj.item && !trendObj.isDestination) {
          finalTrendDataForApi.location = trendObj.item.value;
        }
        dispatch(addTrend(finalTrendDataForApi)); // Replace with actual dispatch
      }
    } else {
      // Handling for 'ticket' (Item) or 'recommendation'
      if (updatedValues.type === "item" && updatedValues.item) {
        finalTrendDataForApi = {
          type: "item",
          itemId: updatedValues.item,
          trendingStart: updatedValues.trendingStart,
        };
      } else if (
        updatedValues.type === "recommendation" &&
        updatedValues.recommendation &&
        updatedValues.recommendation._id
      ) {
        finalTrendDataForApi = {
          type: "recommendation",
          itemId: updatedValues.recommendation._id,
          trendingStart: updatedValues.trendingStart,
        };
      } else {
        return;
      }

      // Add common fields if an item or recommendation was processed
      if (Object.keys(finalTrendDataForApi).length > 0) {
        if (updatedValues.trendingEnd && updatedValues.trendingEnd.length > 0) {
          finalTrendDataForApi.trendingEnd = updatedValues.trendingEnd;
        }
        // Add location ID if trendObj.value exists and it's NOT a destination
        if (trendObj?.item && !trendObj.isDestination) {
          finalTrendDataForApi.location = trendObj.item.value;
        }

        dispatch(addTrend(finalTrendDataForApi));
        actions.setSubmitting(false);
        actions.resetForm();
        setActiveStep(0);
      }
    }

    navigate(
      `/admin/uniqueExperiences?itemId=${trendObj?.item?.value}&itemLabel=${encodeURIComponent(
        trendObj?.item?.label
      )}&isDestination=${trendObj?.isDestination}&isTrip=${trendObj?.isTrip}`
    );
    if (Object.keys(finalTrendDataForApi).length > 0) {
      actions.setSubmitting(false);
      actions.resetForm();
      setActiveStep(0);
    } else {
      actions.setSubmitting(false);
    }
  };

  const handleRecommendationChange = (value) => {
    if (value && value?._id) {
      setSelectedRecommendation(value);
      setSelectedItem({});
    } else {
      setSelectedRecommendation({});
    }
  };

  const handleItemChange = (value) => {
    if (value && value?.label) {
      setSelectedItem(value);
      setSelectedRecommendation({});
      // dispatch(getOneItem(value?.value));
    } else {
      setSelectedItem({});
    }
  };

  const handleItineraryChange = (value) => {
    if (value && value?._id) {
      setSelectedItenerary(value);
    } else {
      setSelectedItenerary({});
    }
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
      scrollToTop(); // Scroll to the top
    }
  };

  // useEffect
  useEffect(() => {
    setSteps(getSteps("admin"));
  }, []);

  useHandleError(error, dispatch, setAlert, clearErrors); // Use the custom hook

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} mb={20}>
        <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%", mt: 8 }}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Formik
              initialValues={initialValues}
              // validationSchema={steps[activeStep] === "Review" ? undefined : currentValidation}
              validationSchema={
                steps[activeStep] === "Review" ? undefined : buildSchema(trendObj.isTrip)
              }
              enableReinitialize
              validateOnBlur={false}
              validateOnChange={false}
              onSubmit={handleSubmit}
              innerRef={(f) => {
                formRef.current = f;
              }}
            >
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <MDBox mx={2} mt={-3}>
                      <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                          <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </MDBox>
                    <MDBox p={3}>
                      <MDBox>
                        {getStepContent(
                          activeStep,
                          {
                            values,
                            touched,
                            formField,
                            errors,
                            setFieldValue,
                          },
                          handleItemChange,
                          handleRecommendationChange,
                          handleItineraryChange,
                          selectedItem,
                          selectedRecommendation,
                          selectedItenerary
                        )}
                        <MDBox mt={2} width="100%" display="flex" justifyContent="space-between">
                          {activeStep === 0 ? (
                            <MDBox />
                          ) : (
                            <MDButton variant="gradient" color="light" onClick={handleBack}>
                              back
                            </MDButton>
                          )}
                          <div>
                            <MDButton
                              onClick={() =>
                                navigate(
                                  `/admin/uniqueExperiences?itemId=${
                                    trendObj?.item?.value
                                  }&itemLabel=${encodeURIComponent(
                                    trendObj?.item?.label
                                  )}&isDestination=${trendObj?.isDestination}&isTrip=${
                                    trendObj?.isTrip
                                  }`
                                )
                              }
                              rel="noreferrer"
                              variant="outlined"
                              color="dark"
                              sx={{ marginRight: "8px" }}
                            >
                              Cancel
                            </MDButton>
                            <MDButton
                              disabled={isSubmitting}
                              type="submit"
                              variant="gradient"
                              color="dark"
                            >
                              {isLastStep ? "add" : "next"}
                            </MDButton>
                          </div>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default NewUniqieExperience;
